import React from 'react'
import styled from 'styled-components'
import {
  zoning_indice_sources,
  zoning_sources_agreement,
  ZoningMetricSources,
} from '@popety-io/types'
import { SourceBadgeLine } from './SourceBadgeLine'
import { getVisibleSources } from './utils/metricTableHelpers'

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const Separator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`

interface DualSourceBadgeLineProps {
  metricKey: string
  isOverridden?: boolean
  // Single-bundle (single-value metrics)
  defaultSource?: zoning_indice_sources
  agreement?: zoning_sources_agreement
  sources?: ZoningMetricSources
  // Per-bound (range metrics)
  minDefaultSource?: zoning_indice_sources
  maxDefaultSource?: zoning_indice_sources
  minAgreement?: zoning_sources_agreement
  maxAgreement?: zoning_sources_agreement
  minSources?: ZoningMetricSources
  maxSources?: ZoningMetricSources
  /** Whether the metric currently displays both min and max values */
  hasMinMax?: boolean
}

/**
 * Computes a stable signature for a bound's badge-line content.
 * Two bounds with matching signatures can be rendered as a single line.
 */
const signatureFor = (
  defaultSource?: zoning_indice_sources,
  agreement?: zoning_sources_agreement,
  sources?: ZoningMetricSources
): string => {
  const visible = getVisibleSources(sources).sort().join(',')
  return `${defaultSource || ''}|${agreement || ''}|${visible}`
}

/**
 * Renders the source badge line(s) below an authorized value.
 * - Non-range or matching bounds → one line.
 * - Range with differing bounds → MIN badge line / MAX badge line on a single
 *   row, mirroring the "min / max" separator used in the value display.
 */
export const DualSourceBadgeLine: React.FC<DualSourceBadgeLineProps> = ({
  metricKey,
  isOverridden,
  defaultSource,
  agreement,
  sources,
  minDefaultSource,
  maxDefaultSource,
  minAgreement,
  maxAgreement,
  minSources,
  maxSources,
  hasMinMax,
}) => {
  const showDual =
    !!hasMinMax &&
    (minSources !== undefined || maxSources !== undefined) &&
    signatureFor(minDefaultSource, minAgreement, minSources) !==
      signatureFor(maxDefaultSource, maxAgreement, maxSources)

  if (!showDual) {
    return (
      <SourceBadgeLine
        defaultSource={minDefaultSource ?? maxDefaultSource ?? defaultSource}
        agreement={minAgreement ?? maxAgreement ?? agreement}
        sources={minSources ?? maxSources ?? sources}
        metricKey={metricKey}
        isOverridden={isOverridden}
      />
    )
  }

  return (
    <InlineRow>
      <SourceBadgeLine
        defaultSource={minDefaultSource}
        agreement={minAgreement}
        sources={minSources}
        metricKey={metricKey}
        isOverridden={isOverridden}
      />
      <Separator>/</Separator>
      <SourceBadgeLine
        defaultSource={maxDefaultSource}
        agreement={maxAgreement}
        sources={maxSources}
        metricKey={metricKey}
        isOverridden={isOverridden}
      />
    </InlineRow>
  )
}
